/* istanbul ignore file */ // https://hello.jira.atlassian.cloud/browse/ITFO-15833
import React from 'react';
import classNames from 'classnames';
import { uniqBy } from 'lodash';
import capitalize from 'lodash/capitalize';
import defaultTo from 'lodash/defaultTo';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import sumBy from 'lodash/sumBy';
import toNumber from 'lodash/toNumber';
import flatten from 'lodash/flatten';
import { Link } from 'react-router-dom';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import { Box, Text } from '@atlaskit/primitives';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import Popup from '@atlaskit/popup';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import {
  AppRows,
  AppSubRow,
  ColGroup,
  CopyableText,
  CustomDynamicResultTable,
  DSTLineItem,
  formattedV2,
  getCASSIBaseURL,
  getEntitlementRouteFromLineItems,
  HumanReadableIdentifierMissingIcon,
  isAccess,
  isResourceAccess,
  NoDataContentInTable,
  OpenSidebarButton,
  SearchResultColumn,
  TableBody,
  TableHead,
  useFeatureToggle,
  FormatCurrency,
} from '@css-xp/base';
import {
  ENABLE_DESIGN_ITERATION_1,
  LINE_ITEMS_TABLE_HEADER_NAMES,
  STATSIG_FEATURE_GATES,
  SUBSCRIPTION_CONSTANTS,
} from '@css-xp/ppc-constants';
import { CloudSiteLink } from '@css-xp/ppc-common/src/cloud-site-link';
import { CloudProductDetailView, EntitlementStatus, getReadablePromotionValue } from '@css-xp/base/entitlement-utils';
import { generatedTypesNew, Promotion } from '@css-xp/graphtypes';
import {
  BaseRoutes,
  BillingDocWithLineItems,
  InvoiceLineItem,
  InvoiceLineItemDiscount,
  TaxItems,
} from '@atlassian/cssxp-types-billing';
import { PARTNER_DISCOUNT } from '@atlassian/cssxp-utils-entitlement';
import { getChargeElement, isCollectionProductEntitlement, isOrgProduct } from '@css-xp/ppc-utils';
import { OrgNameLink } from '@css-xp/ppc-common/src/org-name-link';
import {
  CLOUD_SITE_PATH,
  CLOUD_SITE_PATH_2,
  NEW_DESIGN_DATE_FORMAT,
  Permissions,
  Resources,
  SourceSystem,
} from '@atlassian/cssxp-typescommon';
import { ENABLE_CLOUD_SITE_AC_REDIRECTION } from '@atlassian/cssxp-utils-generic';
import {
  AppsGroupData,
  CreditItemsLinked,
  formatInvoiceLineItems,
  formatInvoiceLineItemsCCP,
  getDstLineItemDetails,
  getSortedInvoiceLineItems,
  isCreditInvoiceLineItem,
  MarginType,
  ProductsGroupContainer,
  getPartnerMarginItems,
  AddOnsGroupData,
  InvoiceWithLineItemsPPC,
  getProcessedLineItems,
} from '../invoice-page-utils';
import InvoiceAmountDetails from './invoice-amount-details';
import RefundResults from './invoice-detail-refund-results';
import { SIDEBAR } from './invoice-page';
import InvoicePaymentDetails from './invoice-payment-details';
import { PartnerMarginModal } from './partner-margin-modal';
import InvoiceViewAdjustmentsPane from './invoice-view-adjustments-pane';
import './invoice-line-items.scss';

enum InvoiceLineItemsTable {
  PRODUCT_DETAILS = 'product_details',
  ORGANIZATION = 'organization',
  SITE = 'site',
  BILLING_PERIOD = 'billing_period',
  USAGE = 'usage',
  LIST_PRICE = 'list_price',
  PARTNER_MARGIN = 'partner_margin',
  DISCOUNT_APPLIED = 'discount_applied',
  AMOUNT_INCL_TAX = 'amount_incl',
  AMOUNT_EXCL_TAX = 'amount_excl',
  CUSTOMER_CONTACTS = 'customer_contacts',
  AMOUNT = 'amount',
}

export const emptyStateProps = {
  header: '',
  description: 'No invoices found',
  imageUrl: '',
};

export const getIdentifier = (item: InvoiceLineItem, sourceSystem?: SourceSystem | null) => {
  if (sourceSystem === SourceSystem.CCP) {
    if (item.entitlementNumber) {
      return <>{item.entitlementNumber}</>;
    }
    return (
      <>
        {item.id} <HumanReadableIdentifierMissingIcon />
      </>
    );
  }
  return <>SEN-{item.sen || item.id}</>;
};

const GetEntitlementUUID = (item: InvoiceLineItem, sourceSystem?: SourceSystem | null) => {
  const hasFinanceUserPermission =
    isAccess(Permissions.SUBSCRIPTION_ENTITLEMENT_UUID_READ) && isResourceAccess(Resources.INVOICE_ALL);
  if (hasFinanceUserPermission && sourceSystem === SourceSystem.CCP) {
    return <CopyableText text={item.id} />;
  }
  return;
};

const InvoiceLineItemDescription = ({
  item,
  sourceSystem,
  entitlementBaseRoute,
  cloudSiteBaseRoute,
  isSandboxEntitlement,
  isAddOnEntitlement,
  showIncludedProductsCTA,
  collectionInstancesIds,
  transactionAccountId,
}: {
  item: InvoiceLineItem;
  sourceSystem?: SourceSystem | null;
  /* The `entitlementBaseRoute` prop is used in apps like PPC to correctly redirect
   * to its entitlement page which has different route structure than AC.
   * Eg.: /any-custom-route-for-entitlement/
   * */
  entitlementBaseRoute?: string;
  cloudSiteBaseRoute?: string;
  isSandboxEntitlement?: boolean;
  isAddOnEntitlement?: boolean;
  showIncludedProductsCTA?: boolean;
  collectionInstancesIds?: string[];
  transactionAccountId?: string | null;
}) => {
  const entitlementRoute = entitlementBaseRoute
    ? entitlementBaseRoute + (item?.entitlement?.id ?? item?.id)
    : getEntitlementRouteFromLineItems(item);

  const [isEnabled] = useFeatureToggle();
  const cassiBaseURL = getCASSIBaseURL();

  const cloudSiteLink = React.useMemo(() => {
    if (cloudSiteBaseRoute) return cloudSiteBaseRoute + item?.cloudSiteId;

    if (isEnabled(ENABLE_CLOUD_SITE_AC_REDIRECTION)) {
      return `${CLOUD_SITE_PATH_2}/${item?.cloudSiteId}`;
    } else {
      // Note: For cassi redirection needed full path name of url which will not append to current url
      return { pathname: `${cassiBaseURL}${CLOUD_SITE_PATH}/${item?.cloudSiteId}` };
    }
  }, [cloudSiteBaseRoute, item]);

  return (
    <div className="invoice-details-col">
      {isCreditInvoiceLineItem(item) && (
        <div data-testid="unused-credit" className="unused-credit">
          Credit for unused paid time
        </div>
      )}
      <Link to={entitlementRoute}>
        <span>
          <strong>{getIdentifier(item, sourceSystem)}</strong>
        </span>
      </Link>
      <div className="entitlement-container">{GetEntitlementUUID(item, sourceSystem)}</div>
      {isSandboxEntitlement && (
        <div className="edition-details">
          <EntitlementStatus isSandbox={isSandboxEntitlement} />
        </div>
      )}
      {isAddOnEntitlement && (
        <div className="edition-details">
          <EntitlementStatus isAddOn={isAddOnEntitlement} />
        </div>
      )}
      <div>
        {item.edition && item.edition.includes(item.productName as string) ? '' : item.productName + ' '}
        {item.edition + ' ' + capitalize(item.pricingPlan?.billingFrequency as string) + ' ' + item.pricingPlan?.type}
      </div>
      {showIncludedProductsCTA && (
        <OpenSidebarButton
          sidebar={SIDEBAR.INCLUDED_PRODUCTS}
          sidebarOptions={{
            sidebarWidth: '20%',
            isOverlay: true,
          }}
          payload={{
            transactionAccountId,
            selectedEntitlementDetails: {
              id: item?.entitlement?.id,
              productName: item?.entitlement?.productName,
              entitlementNumber: item?.entitlement?.entitlementNumber,
            },
            linkedCollectionProductsId: collectionInstancesIds,
            version: 1,
            linkedAppsId: [],
            linkedAddOnsId: [],
            linkedEnterpriseInstancesId: [],
            linkedSandboxId: [],
          }}
          label="Included products"
        >
          {`${collectionInstancesIds?.length} subscription${collectionInstancesIds?.length ?? 0 > 1 ? 's' : ''}`}
        </OpenSidebarButton>
      )}
    </div>
  );
};

const InvoiceLineItemDescriptionV2 = ({
  item,
  shouldHideSite,
  orgNameLink,
  isSandboxEntitlement,
  isAddOnEntitlement,
  entitlementBaseRoute,
  showIncludedProductsCTA,
  collectionInstancesIds,
  transactionAccountId,
}: {
  item: InvoiceLineItem;
  shouldHideSite?: boolean;
  orgNameLink?: JSX.Element;
  isSandboxEntitlement?: boolean;
  isAddOnEntitlement?: boolean;
  entitlementBaseRoute?: string;
  showIncludedProductsCTA?: boolean;
  collectionInstancesIds?: string[];
  transactionAccountId?: string | null;
}) => {
  const entitlementId = item?.id || item?.entitlementNumber;

  return (
    <div>
      {isCreditInvoiceLineItem(item) && (
        <div data-testid="unused-credit" className="unused-credit">
          Credit for unused paid time
        </div>
      )}
      <CloudProductDetailView
        logoId={item?.logoId as string}
        productId={item?.productKey as string}
        entitlementNumber={item.entitlementNumber ?? ''}
        productName={item?.productName}
        cloudSiteId={item?.cloudSiteId}
        productLink={entitlementBaseRoute ? `${entitlementBaseRoute}${entitlementId}` : entitlementId ?? ''}
        version={2}
        isSandbox={isSandboxEntitlement}
        isAddOn={isAddOnEntitlement}
      />
      <div className="invoice-product-details pt-2" data-testId="product-edition-plan">
        {item.edition}{' '}
        {item.pricingPlan?.billingFrequency && `| ${capitalize(item.pricingPlan?.billingFrequency as string)}`}{' '}
        {item.pricingPlan?.type && `| ${capitalize(item.pricingPlan?.type)}`}
      </div>
      <div className="invoice-product-details" data-testId="product-billing-period">
        Billing period: <formattedV2.FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={item.startDate} />
        {` - `}
        <formattedV2.FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={item.endDate} />
      </div>
      <div className="invoice-product-details" data-testId="product-usage">
        Usage: {item.quantity ?? 0}{' '}
        {getChargeElement({
          productId: item?.productKey,
          labelPlural: Number(item?.quantity) !== 1,
          edition: item?.edition,
        })}
      </div>
      {!shouldHideSite && item?.entitlement?.cloudSiteUrl && (
        <div className="invoice-product-details" data-testid="site-url">
          Site: {item?.entitlement?.cloudSiteUrl}
        </div>
      )}
      {orgNameLink && <div className="invoice-product-details">Organization: {orgNameLink}</div>}
      {showIncludedProductsCTA && (
        <Box paddingInline="space.400" paddingBlockStart="space.100">
          <OpenSidebarButton
            sidebar={SIDEBAR.INCLUDED_PRODUCTS}
            payload={{
              transactionAccountId,
              selectedEntitlementDetails: {
                id: item?.entitlement?.id,
                productName: item?.entitlement?.productName,
                entitlementNumber: item?.entitlement?.entitlementNumber,
              },
              linkedCollectionProductsId: collectionInstancesIds,
              linkedAppsId: [],
              linkedAddOnsId: [],
              linkedEnterpriseInstancesId: [],
              linkedSandboxId: [],
              version: 2,
            }}
            label="Included products"
          >
            {`${collectionInstancesIds?.length} subscription${collectionInstancesIds?.length ?? 0 > 1 ? 's' : ''}`}
          </OpenSidebarButton>
        </Box>
      )}
    </div>
  );
};

/**
 * Component is type casted to be used in BillingDoc Component having details of the invoice
 * @param entitlementBaseRoute as string
 * @param refundsBaseRoute as string
 * @param cloudSiteBaseRoute as string
 * @param shouldDisableRefundsSidebar as boolean
 * @param rest as billingDoc
 * @constructor
 */
const InvoiceLineItems = (
  billingDoc: BillingDocWithLineItems & BaseRoutes & { shouldDisableRefundsSidebar?: boolean }
) => {
  const {
    entitlementBaseRoute,
    refundsBaseRoute,
    cloudSiteBaseRoute,
    shouldDisableRefundsSidebar,
    isPaymentMethodDetailsHidden,
    ...rest
  } = billingDoc;
  const invoice = rest as InvoiceWithLineItemsPPC;
  return (
    <StatelessInvoiceLineItems
      invoice={invoice}
      entitlementBaseRoute={entitlementBaseRoute}
      refundsBaseRoute={refundsBaseRoute}
      cloudSiteBaseRoute={cloudSiteBaseRoute}
      shouldDisableRefundsSidebar={shouldDisableRefundsSidebar}
      isPaymentMethodDetailsHidden={isPaymentMethodDetailsHidden}
    />
  );
};

export const getPromotionReasonWithDescription = (promotion: Promotion, discount: InvoiceLineItemDiscount | null) => {
  return (
    <div data-testid="promotion-invoice-note">
      {promotion?.title && (
        <div>
          {promotion?.title} (
          {discount?.percent ? discount?.percent + '% discount' : getReadablePromotionValue(promotion as Promotion)})
        </div>
      )}
    </div>
  );
};

const adjustedAmount = (
  subtotal: string | number | null | undefined,
  discount: InvoiceLineItemDiscount[] | null,
  margins?: number | undefined | null
): number => {
  const actualSubtotal = toNumber(subtotal);
  const actualDiscount = sumBy(discount, 'amount');
  const partnerMargin = defaultTo(margins, 0);
  if (!isNaN(actualSubtotal) && actualSubtotal !== 0 && !isNaN(actualDiscount)) {
    return actualSubtotal - actualDiscount - partnerMargin;
  }

  return 0;
};

const getColumns = (
  sourceSystem: SourceSystem | null | undefined,
  canShowPromotionOnInvoice: boolean,
  isCustomerContactEnabled?: boolean,
  shouldShowPartnerMarginColumn?: boolean,
  currency?: string | null | undefined,
  isDesignIteration1Enabled?: boolean,
  isRedesignFastFollowEnabled?: boolean
  // eslint-disable-next-line max-params
): SearchResultColumn[] => {
  let columns = [];
  const currencyCode = isDesignIteration1Enabled && currency ? ` (${currency})` : ``;
  const headers = {
    [InvoiceLineItemsTable.PRODUCT_DETAILS]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.DETAILS,
      customClassNames:
        isDesignIteration1Enabled && shouldShowPartnerMarginColumn
          ? isRedesignFastFollowEnabled
            ? 'col-20'
            : 'col-30'
          : 'col-40',
    },
    [InvoiceLineItemsTable.ORGANIZATION]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.ORGANIZATION,
      customClassNames: 'col-40',
    },
    [InvoiceLineItemsTable.SITE]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.SITE,
      customClassNames: isRedesignFastFollowEnabled ? 'col-18' : 'col-40',
    },
    [InvoiceLineItemsTable.BILLING_PERIOD]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.BILLING_PERIOD,
      customClassNames: 'col-30',
    },
    [InvoiceLineItemsTable.USAGE]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.USAGE,
      customClassNames: 'col-20',
    },
    [InvoiceLineItemsTable.CUSTOMER_CONTACTS]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.CUSTOMER_CONTACTS,
      customClassNames: 'col-30',
    },
    [InvoiceLineItemsTable.LIST_PRICE]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.LIST_PRICE,
      customClassNames: isDesignIteration1Enabled ? (isRedesignFastFollowEnabled ? 'col-12' : 'col-10') : 'col-20',
      ...(isDesignIteration1Enabled
        ? {
            renderComponent: (
              <th className="currency-code-header">{`${LINE_ITEMS_TABLE_HEADER_NAMES.LIST_PRICE} ${currencyCode}`}</th>
            ),
          }
        : {}),
    },
    [InvoiceLineItemsTable.PARTNER_MARGIN]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.PARTNER_MARGIN,
      customClassNames: isDesignIteration1Enabled ? (isRedesignFastFollowEnabled ? 'col-12-5' : 'col-15') : 'col-20',
      ...(isDesignIteration1Enabled
        ? {
            renderComponent: (
              <th className="currency-code-header">
                {`${LINE_ITEMS_TABLE_HEADER_NAMES.PARTNER_MARGIN} ${currencyCode}`}
              </th>
            ),
          }
        : {}),
    },
    [InvoiceLineItemsTable.DISCOUNT_APPLIED]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.DISCOUNTS_APPLIED,
      customClassNames: isDesignIteration1Enabled ? (isRedesignFastFollowEnabled ? 'col-12-5' : 'col-15') : 'col-20',
      ...(isDesignIteration1Enabled
        ? {
            renderComponent: <th className="currency-code-header">{`Other discounts ${currencyCode}`}</th>,
          }
        : {}),
    },
    [InvoiceLineItemsTable.AMOUNT_INCL_TAX]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_INCL_TAX,
      customClassNames: isDesignIteration1Enabled ? (isRedesignFastFollowEnabled ? 'col-12-5' : 'col-15') : 'col-20',
      ...(isDesignIteration1Enabled
        ? {
            renderComponent: (
              <th className="currency-code-header">
                {`${LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_INCL_TAX} ${currencyCode}`}
              </th>
            ),
          }
        : {}),
    },
    [InvoiceLineItemsTable.AMOUNT_EXCL_TAX]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_EXCL_TAX,
      customClassNames: isDesignIteration1Enabled ? (isRedesignFastFollowEnabled ? 'col-12-5' : 'col-15') : 'col-20',
      ...(isDesignIteration1Enabled
        ? {
            renderComponent: (
              <th className="currency-code-header">
                {`${LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT_EXCL_TAX} ${currencyCode}`}
              </th>
            ),
          }
        : {}),
    },
    [InvoiceLineItemsTable.AMOUNT]: {
      name: LINE_ITEMS_TABLE_HEADER_NAMES.AMOUNT,
      customClassNames: 'col-20',
    },
  };

  if (canShowPromotionOnInvoice && sourceSystem === SourceSystem.CCP) {
    if (isDesignIteration1Enabled) {
      columns = [
        { ...headers[InvoiceLineItemsTable.PRODUCT_DETAILS], name: LINE_ITEMS_TABLE_HEADER_NAMES.SUBSCRIPTION },
        ...(isRedesignFastFollowEnabled ? [headers[InvoiceLineItemsTable.SITE]] : []),
        headers[InvoiceLineItemsTable.LIST_PRICE],
        ...(shouldShowPartnerMarginColumn ? [headers[InvoiceLineItemsTable.PARTNER_MARGIN]] : []),
        headers[InvoiceLineItemsTable.DISCOUNT_APPLIED],
        headers[InvoiceLineItemsTable.AMOUNT_EXCL_TAX],
        headers[InvoiceLineItemsTable.AMOUNT_INCL_TAX],
      ];
    } else {
      columns = [
        headers[InvoiceLineItemsTable.PRODUCT_DETAILS],
        headers[InvoiceLineItemsTable.ORGANIZATION],
        headers[InvoiceLineItemsTable.SITE],
        ...(isCustomerContactEnabled ? [headers[InvoiceLineItemsTable.CUSTOMER_CONTACTS]] : []),
        headers[InvoiceLineItemsTable.BILLING_PERIOD],
        headers[InvoiceLineItemsTable.USAGE],
        headers[InvoiceLineItemsTable.LIST_PRICE],
        ...(shouldShowPartnerMarginColumn ? [headers[InvoiceLineItemsTable.PARTNER_MARGIN]] : []),
        headers[InvoiceLineItemsTable.DISCOUNT_APPLIED],
        headers[InvoiceLineItemsTable.AMOUNT_EXCL_TAX],
        headers[InvoiceLineItemsTable.AMOUNT_INCL_TAX],
        {
          name: '',
          customClassNames: 'col-2',
        },
      ];
    }
  } else {
    columns = [
      headers[InvoiceLineItemsTable.PRODUCT_DETAILS],
      ...(isCustomerContactEnabled ? [headers[InvoiceLineItemsTable.CUSTOMER_CONTACTS]] : []),
      headers[InvoiceLineItemsTable.BILLING_PERIOD],
      headers[InvoiceLineItemsTable.AMOUNT],
      {
        name: '',
        customClassNames: 'col-2',
      },
    ];
  }
  return columns;
};

export const getTaxRate = (taxItems: TaxItems[] | null | undefined) => {
  const nonZeroTaxItems = taxItems?.filter((taxItem: TaxItems) => {
    const tax = toNumber(taxItem.tax);
    if (!tax || isNaN(tax)) return false;
    return true;
  });

  if (!nonZeroTaxItems?.length) return null;
  return nonZeroTaxItems?.map((taxItem: TaxItems, index) => {
    return (
      <>
        <div key={index}>{toNumber(taxItem.taxPercent) + '% ' + taxItem.taxAmountLabel}</div>
      </>
    );
  });
};

/**
 * StatelessInvoiceLineItems component to show invoice line items in side pane as well as full page view
 * In sidebar pane, we don't have to show child products in case of HAMS
 * In full view page, we show products as well
 */

export const StatelessInvoiceLineItems = ({
  txaId,
  invoice,
  entitlementBaseRoute,
  refundsBaseRoute,
  cloudSiteBaseRoute,
  shouldDisableRefundsSidebar,
  isPaymentMethodDetailsHidden = false,
  renderCustomerContacts,
  isCustomerContactEnabled,
  isSortLineItemsEnabled,
  isSupportForOverageEnabled = false,
  shouldGroupAddOns = false,
  isMultiCurrencyChangesEnabled = false,
  isTWCPhase1Enabled = false,
}: {
  txaId?: string;
  invoice: InvoiceWithLineItemsPPC;
  entitlementBaseRoute?: string;
  refundsBaseRoute?: string;
  cloudSiteBaseRoute?: string;
  shouldDisableRefundsSidebar?: boolean;
  isPaymentMethodDetailsHidden?: boolean;
  renderCustomerContacts?: (_entitlementId: string) => JSX.Element;
  isCustomerContactEnabled?: boolean;
  isSortLineItemsEnabled?: boolean;
  isSupportForOverageEnabled?: boolean;
  shouldGroupAddOns?: boolean;
  isMultiCurrencyChangesEnabled?: boolean;
  isTWCPhase1Enabled?: boolean;
}): JSX.Element => {
  const canShowPromotionOnInvoice = true; //useSelector(getFeatureFlag(PROMOTIONS_ON_INVOICE));
  const [isOpen, setIsOpen] = React.useState(false);
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isPartnerMarginColumnEnabledForAllBillingCycle = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_PARTNER_MARGIN_COLUMN_FOR_ANY_BILLING_CYCLE
  );
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isRedesignFastFollowEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_REDESIGN_FAST_FOLLOW);

  const currency = isMultiCurrencyChangesEnabled ? invoice?.pricingCurrency : invoice.paymentCurrency;
  const getLineItemKey = (item: InvoiceLineItem) => {
    // All these fields are potentially null so use the
    // combination of them to create a unique key for React
    const identifier = invoice.sourceSystem === SourceSystem.CCP ? item.entitlementNumber || item.id : item.sen;
    const description = item.description ?? '';
    const amount = item.amount ?? '';

    return `${identifier}-${description}-${amount}`;
  };

  const isAnyOfEntitlementsOfAnnual = invoice?.lineItems?.some((lineItem) => {
    return (
      lineItem?.entitlement?.billingCycle === generatedTypesNew.BillingFrequency.ANNUAL ||
      lineItem?.pricingPlan?.billingFrequency === generatedTypesNew.BillingFrequency.ANNUAL
    );
  });
  const shouldShowPartnerMarginColumn = isPartnerMarginColumnEnabledForAllBillingCycle || isAnyOfEntitlementsOfAnnual;

  const isCCPInvoice = invoice.sourceSystem === SourceSystem.CCP;
  const lineItems: InvoiceLineItem[] = isCCPInvoice
    ? formatInvoiceLineItemsCCP(invoice.lineItems)
    : formatInvoiceLineItems(invoice.lineItems);

  const getAdjustmentTotal = (discounts: InvoiceLineItemDiscount[] | null) => {
    if (!discounts) return 0;
    return discounts.reduce((amount, a) => amount + (a.amount as number), 0);
  };

  const getPartnerDiscountsTotal = (
    discounts:
      | (InvoiceLineItemDiscount & {
          type?: string | null;
        })[]
      | null
  ) => {
    if (!discounts) return 0;
    return discounts
      .filter((discount) => discount.type === PARTNER_DISCOUNT)
      .reduce((amount, a) => amount + (a.amount as number), 0);
  };

  const getNonPartnerDiscountsTotal = (
    discounts:
      | (InvoiceLineItemDiscount & {
          type?: string | null;
        })[]
      | null
  ) => {
    if (!discounts) return 0;
    return discounts
      .filter((discount) => discount.type !== PARTNER_DISCOUNT)
      .reduce((amount, a) => amount + (a.amount as number), 0);
  };

  const getTotalMarginAmount = (margins: any) => {
    if (!margins) return 0;
    return margins?.reduce((totalAmount: number, margin: any) => {
      if (margin?.type === MarginType.PARTNER_MARGIN) {
        return totalAmount + margin?.amount;
      }
      return totalAmount;
    }, 0);
  };

  const getTaxAmountTotal = (
    taxItems: TaxItems[] | null | undefined,
    taxAmountPaymentCurrency: string | number | null | undefined
  ) => {
    if (!taxItems && !taxAmountPaymentCurrency) return 0;
    if (!taxItems && taxAmountPaymentCurrency) return toNumber(taxAmountPaymentCurrency);
    return taxItems?.reduce((amount, taxItem) => amount + toNumber(taxItem.tax), 0) ?? 0;
  };

  const coupledLineItems = invoice.coupledLineItems as generatedTypesNew.ProductAppLineItemData;

  const sortedCoupledlineItems = React.useMemo(() => {
    return coupledLineItems ? getSortedInvoiceLineItems(coupledLineItems) : null;
  }, [coupledLineItems]);

  const processedLineItems = React.useMemo(() => {
    return lineItems && isTWCPhase1Enabled
      ? getProcessedLineItems(lineItems as generatedTypesNew.InvoiceLineItems[])
      : null;
  }, [lineItems, isTWCPhase1Enabled]);

  const getDstLineItem = (
    invoice: InvoiceWithLineItemsPPC,
    tableHeaders: SearchResultColumn[],
    currency?: string | null
  ) => {
    if (isCCPInvoice && invoice?.dstLineItems?.length) {
      const { subtotal, taxAmountPaymentCurrency, dstTaxItems } = getDstLineItemDetails(invoice.dstLineItems);
      const amountExclTax = adjustedAmount(subtotal, null);
      const amountInclTax = adjustedAmount(subtotal, null) + getTaxAmountTotal(dstTaxItems, taxAmountPaymentCurrency);
      const uniqTaxItems = uniqBy(dstTaxItems, 'taxAmountLabel');
      const taxRate = getTaxRate(uniqTaxItems);
      return (
        <DSTLineItem
          totalExTax={amountExclTax}
          totalIncTax={amountInclTax}
          currency={currency}
          taxRate={taxRate}
          tableHeaders={tableHeaders}
          {...(isDesignIteration1Enabled && { className: 'dst-line-item-v2' })}
          showCommas
          hideCurrencyCode={isDesignIteration1Enabled}
        />
      );
    }
    return null;
  };

  const renderOrgName = (item: any) => {
    return (
      <OrgNameLink
        orgName={item?.orgInfo?.orgName}
        orgId={item?.orgInfo?.orgId}
        txaId={txaId}
        NoDataFallback={<NoDataContentInTable />}
      />
    );
  };

  const renderViewDiscountsSidebar = (payload: any) => {
    const renderSidebarButton = () => (
      <OpenSidebarButton
        sidebar={SIDEBAR.INVOICE_VIEW_ADJUSTMENTS}
        sidebarOptions={
          isDesignIteration1Enabled
            ? {
                sidebarTitle: payload.entitlementNumber,
              }
            : {
                sidebarWidth: '968px',
                isOverlay: true,
              }
        }
        payload={payload}
        appearance="link"
        testId="view-total-adjustments"
        label="View discounts"
      >
        View discounts
      </OpenSidebarButton>
    );

    return isDesignIteration1Enabled ? (
      <Popup
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        placement="bottom-start"
        zIndex={2000}
        content={() => (
          <>
            <Box paddingBlockStart="space.300" paddingInlineStart="space.300">
              <Heading size="xsmall">Other discounts applied</Heading>
            </Box>
            <InvoiceViewAdjustmentsPane {...payload} />
          </>
        )}
        trigger={(triggerProps) => (
          <Button
            {...triggerProps}
            testId="view-partner-discounts-details-button"
            iconBefore={<WatchIcon label="View details" size="small" />}
            onClick={() => setIsOpen(!isOpen)}
            className="view-partner-discounts-details-button"
          />
        )}
      />
    ) : (
      <Tooltip content="View discounts" position="bottom" hideTooltipOnClick>
        {renderSidebarButton()}
      </Tooltip>
    );
  };

  const getColumnData = (item: any, column: string, shouldHideSite?: boolean) => {
    const isOrgLevelProduct = isOrgProduct({
      productIdOrKey: item?.productKey,
      offeringName: item?.edition,
      relatesFromEntitlements: item?.essotEntitlement?.relatesFromEntitlements,
    });
    const isSandboxEntitlement = isSandboxSupportForSubscriptionsEnabled && item?.entitlement?.isSandbox;
    const isAddOnEntitlement = item?.entitlement?.isAddOn;

    const itemDiscountPayload = {
      invoiceItem: item,
      discounts: item.discounts,
      promotionItems: item.promotionItems,
      entitlementNumber: item.entitlementNumber,
      currency: invoice.paymentCurrency,
    };

    const isCollectionEntitlement = isCollectionProductEntitlement(item?.entitlement?.productId);

    let collectionInstancesIds: string[] = [];
    if (isCollectionEntitlement) {
      collectionInstancesIds = item?.entitlement?.relatesToEntitlements
        ?.filter(
          (entitlementRelation: any) =>
            (entitlementRelation?.relationshipType as unknown) === SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE
        )
        ?.map((entitlementRelation: any) => entitlementRelation?.entitlementId);
    }

    switch (column) {
      case InvoiceLineItemsTable.PRODUCT_DETAILS:
        return (
          <td>
            {isDesignIteration1Enabled ? (
              <>
                <InvoiceLineItemDescriptionV2
                  item={item}
                  shouldHideSite={shouldHideSite || isRedesignFastFollowEnabled}
                  orgNameLink={isOrgLevelProduct ? renderOrgName(item) : undefined}
                  isSandboxEntitlement={isSandboxEntitlement}
                  isAddOnEntitlement={isAddOnEntitlement}
                  entitlementBaseRoute={entitlementBaseRoute}
                  showIncludedProductsCTA={isTWCPhase1Enabled && isCollectionEntitlement}
                  collectionInstancesIds={collectionInstancesIds}
                  transactionAccountId={invoice.transactionAccountId}
                />
              </>
            ) : (
              <InvoiceLineItemDescription
                item={item}
                sourceSystem={invoice.sourceSystem}
                entitlementBaseRoute={entitlementBaseRoute}
                cloudSiteBaseRoute={cloudSiteBaseRoute}
                isSandboxEntitlement={isSandboxEntitlement}
                isAddOnEntitlement={isAddOnEntitlement}
                showIncludedProductsCTA={isTWCPhase1Enabled && isCollectionEntitlement}
                collectionInstancesIds={collectionInstancesIds}
                transactionAccountId={invoice.transactionAccountId}
              />
            )}
          </td>
        );
      case InvoiceLineItemsTable.ORGANIZATION:
        return <td>{isOrgLevelProduct ? renderOrgName(item) : <NoDataContentInTable />}</td>;
      case InvoiceLineItemsTable.SITE:
        return isRedesignFastFollowEnabled ? (
          <td>
            <CloudSiteLink
              siteName={item?.cloudSiteHostname}
              txaId={txaId}
              cloudSiteId={item?.cloudSiteId}
              shouldRemoveLineClamp={true}
              isOrgProductEntitlement
            />
          </td>
        ) : (
          <td>{item?.entitlement?.cloudSiteUrl || <NoDataContentInTable />}</td>
        );
      case InvoiceLineItemsTable.CUSTOMER_CONTACTS:
        return <td>{item.entitlementId && renderCustomerContacts?.(item.entitlementId)}</td>;
      case InvoiceLineItemsTable.BILLING_PERIOD:
        return (
          <td>
            <formattedV2.FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={item.startDate} />
            {` - `}
            <formattedV2.FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={item.endDate} />
          </td>
        );
      case InvoiceLineItemsTable.USAGE:
        return (
          <td data-testid="quantity-cell">
            <span>
              {item.quantity ?? 0}{' '}
              {getChargeElement({
                productId: item?.productKey,
                labelPlural: Number(item?.quantity) !== 1,
                edition: item?.edition,
              })}
            </span>
          </td>
        );
      case InvoiceLineItemsTable.LIST_PRICE:
        return (
          <td
            className={classNames({
              'currency-code-header': isDesignIteration1Enabled,
            })}
            data-testid="item-amount-cell"
          >
            <FormatCurrency
              value={item.subtotal ? item.subtotal : 0}
              currency={currency}
              showCurrencyCode={!isDesignIteration1Enabled}
              showCommas
            />
            {item?.pricingPlanId && (
              <div className="view-price-plan-link">
                <Tooltip content="View plan" position="bottom" hideTooltipOnClick>
                  <OpenSidebarButton
                    sidebar={SIDEBAR.VIEW_PRICING_DETAILS}
                    sidebarOptions={
                      isDesignIteration1Enabled
                        ? {
                            sidebarTitle: 'Pricing',
                          }
                        : {
                            sidebarWidth: '968px',
                            isOverlay: true,
                          }
                    }
                    payload={{
                      entitlementId: item.entitlementId,
                      entitlementNumber: item.entitlementNumber,
                      billingFrequency: item.pricingPlan?.billingFrequency,
                      productId: item.productKey,
                      productName: item.productName,
                      offeringName: item.edition,
                      pricingPlan: item.pricingPlan,
                      activeUsers: item.quantity,
                      isInvoicePage: true,
                      logoId: item.logoId,
                      ...(isDesignIteration1Enabled && { version: 2 }),
                    }}
                    appearance="link"
                    testId="view-price-plan"
                    label="View plan"
                  >
                    View plan
                  </OpenSidebarButton>
                </Tooltip>
              </div>
            )}{' '}
          </td>
        );
      case InvoiceLineItemsTable.PARTNER_MARGIN: {
        // If the feature flag for showing the partner margin column for all billing cycles is enabled
        // And the entitlements are not of annual billing cycle, we will show only partner discounts under this column
        if (isPartnerMarginColumnEnabledForAllBillingCycle) {
          const isAnnualLineItem =
            item?.entitlement?.billingCycle === generatedTypesNew.BillingFrequency.ANNUAL ||
            item?.pricingPlan?.billingFrequency === generatedTypesNew.BillingFrequency.ANNUAL;
          const discountType = item.isOverageItem || !isAnnualLineItem ? PARTNER_DISCOUNT : MarginType.PARTNER_MARGIN;
          const discountAttribute = item.isOverageItem || !isAnyOfEntitlementsOfAnnual ? 'discounts' : 'margins';
          const partnerDiscounts = item?.[discountAttribute]?.filter((discount: any) => discount.type === discountType);
          const partnerPromotionItems = item.promotionItems?.filter(
            (promotionItem: any) => promotionItem?.applicationReason?.readableName === discountType
          );
          const totalPartnerDiscountOrMargin =
            discountType === PARTNER_DISCOUNT
              ? getPartnerDiscountsTotal(item?.discounts)
              : getTotalMarginAmount(item?.margins);
          const partnerMarginItems = isAnnualLineItem ? getPartnerMarginItems(item?.margins) : [];
          const partnerDiscountPayload = {
            invoiceItem: item,
            discounts: partnerDiscounts,
            promotionItems: partnerPromotionItems,
            entitlementNumber: item.entitlementNumber,
            currency: isMultiCurrencyChangesEnabled ? invoice.pricingCurrency : invoice.paymentCurrency,
          };

          return (
            <>
              <td
                className={classNames({
                  'currency-code-header': isDesignIteration1Enabled,
                })}
              >
                <div>
                  <FormatCurrency
                    value={totalPartnerDiscountOrMargin}
                    currency={currency}
                    showCurrencyCode={!isDesignIteration1Enabled}
                    showCommas
                  />
                  {totalPartnerDiscountOrMargin > 0 && !item?.isOverageItem && !isAnnualLineItem && (
                    <div className="total-adjustments-link">{renderViewDiscountsSidebar(partnerDiscountPayload)}</div>
                  )}
                  {isAnnualLineItem && partnerMarginItems?.length === 1 && (
                    <PartnerMarginModal
                      margin={partnerMarginItems[0]}
                      startDate={item.startDate}
                      endDate={item.endDate}
                      currency={currency}
                    />
                  )}
                </div>
              </td>
            </>
          );
        }
        const partnerMarginItems = getPartnerMarginItems(item?.margins);
        return (
          <td
            className={classNames({
              'currency-code-header': isDesignIteration1Enabled,
            })}
          >
            <div>
              <FormatCurrency
                value={getTotalMarginAmount(item?.margins)}
                currency={currency}
                showCurrencyCode={!isDesignIteration1Enabled}
                showCommas
              />
            </div>
            {partnerMarginItems?.length === 1 && (
              <PartnerMarginModal
                margin={partnerMarginItems[0]}
                startDate={item.startDate}
                endDate={item.endDate}
                currency={currency}
              />
            )}
          </td>
        );
      }
      case InvoiceLineItemsTable.DISCOUNT_APPLIED:
        // If the feature flag for showing the partner margin column for all billing cycles is enabled
        // We will only show the non-partner discounts in the "Discounts applied" column
        if (isPartnerMarginColumnEnabledForAllBillingCycle) {
          const nonPartnerDiscounts = item?.discounts.filter((discount: any) => discount.type !== PARTNER_DISCOUNT);
          const nonPartnerPromotionItems = item.promotionItems?.filter(
            (promotionItem: any) => promotionItem?.applicationReason?.readableName !== PARTNER_DISCOUNT
          );
          const itemWithNonPartnerDiscounts = {
            ...item,
            discounts: nonPartnerDiscounts,
          };

          const notPartnerDiscountPayload = {
            invoiceItem: itemWithNonPartnerDiscounts,
            discounts: nonPartnerDiscounts,
            promotionItems: nonPartnerPromotionItems,
            entitlementNumber: item.entitlementNumber,
            currency: invoice.paymentCurrency,
          };

          return (
            <td
              className={classNames({
                'currency-code-header': isDesignIteration1Enabled,
              })}
            >
              <FormatCurrency
                value={getNonPartnerDiscountsTotal(item.discounts)}
                currency={currency}
                showCurrencyCode={!isDesignIteration1Enabled}
                showCommas
              />
              {getNonPartnerDiscountsTotal(item.discounts) > 0 && (
                <div className="total-adjustments-link">{renderViewDiscountsSidebar(notPartnerDiscountPayload)}</div>
              )}
            </td>
          );
        }

        return (
          <td
            className={classNames({
              'currency-code-header': isDesignIteration1Enabled,
            })}
          >
            <FormatCurrency
              value={getAdjustmentTotal(item.discounts)}
              currency={currency}
              showCurrencyCode={!isDesignIteration1Enabled}
              showCommas
            />
            {getAdjustmentTotal(item.discounts) > 0 && !item.isOverageItem && (
              <div className="total-adjustments-link">{renderViewDiscountsSidebar(itemDiscountPayload)}</div>
            )}
          </td>
        );
      case InvoiceLineItemsTable.AMOUNT_EXCL_TAX:
        // If the feature flag for showing the partner margin column for all billing cycles is enabled
        if (isPartnerMarginColumnEnabledForAllBillingCycle) {
          // If any of the entitlements are of annual billing cycle
          if (isAnyOfEntitlementsOfAnnual) {
            return (
              <td
                className={classNames({
                  'currency-code-header': isDesignIteration1Enabled,
                })}
              >
                <FormatCurrency
                  value={
                    item.isOverageItem
                      ? adjustedAmount(item.totalAccruedSubtotal, item.discounts, item.totalMarginsApplied)
                      : adjustedAmount(item.subtotal, item.discounts, getTotalMarginAmount(item?.margins))
                  }
                  currency={currency}
                  showCurrencyCode={!isDesignIteration1Enabled}
                  showCommas
                />
              </td>
            );
          }
          // If none of the entitlements are of annual billing cycle
          return (
            <td
              className={classNames({
                'currency-code-header': isDesignIteration1Enabled,
              })}
            >
              <FormatCurrency
                value={
                  item.isOverageItem
                    ? adjustedAmount(item.totalAccruedSubtotal, item.discounts)
                    : adjustedAmount(item.subtotal, item.discounts)
                }
                currency={currency}
                showCurrencyCode={!isDesignIteration1Enabled}
                showCommas
              />
            </td>
          );
        }
        // Existing functionality. We are not touching this part of the code to implement.
        // Partner margin column for all billing cycles
        return shouldShowPartnerMarginColumn ? (
          <td
            className={classNames({
              'currency-code-header': isDesignIteration1Enabled,
            })}
          >
            <FormatCurrency
              value={
                item.isOverageItem
                  ? adjustedAmount(item.totalAccruedSubtotal, item.discounts, item.totalMarginsApplied)
                  : adjustedAmount(item.subtotal, item.discounts, getTotalMarginAmount(item?.margins))
              }
              currency={currency}
              showCurrencyCode={!isDesignIteration1Enabled}
              showCommas
            />
          </td>
        ) : (
          <td
            className={classNames({
              'currency-code-header': isDesignIteration1Enabled,
            })}
          >
            <FormatCurrency
              value={
                item.isOverageItem
                  ? adjustedAmount(item.totalAccruedSubtotal, item.discounts)
                  : adjustedAmount(item.subtotal, item.discounts)
              }
              currency={currency}
              showCurrencyCode={!isDesignIteration1Enabled}
              showCommas
            />
          </td>
        );
      case InvoiceLineItemsTable.AMOUNT_INCL_TAX: {
        const taxRate = getTaxRate(item?.taxItems);
        return (
          <td
            className={classNames('amount-incl-tax', {
              'currency-code-header': isDesignIteration1Enabled,
            })}
          >
            {' '}
            <FormatCurrency
              value={
                item.isOverageItem
                  ? adjustedAmount(item.totalAccruedSubtotal, item.discounts, item.totalMarginsApplied) +
                    item.totalTaxApplied
                  : adjustedAmount(item.subtotal, item.discounts, getTotalMarginAmount(item?.margins)) +
                    getTaxAmountTotal(item.taxItems, item.taxAmountPaymentCurrency)
              }
              currency={currency}
              showCurrencyCode={!isDesignIteration1Enabled}
              showCommas
            />
            {taxRate && (
              <span data-testid="tax-tooltip" className="tax-tooltip">
                <Tooltip content={taxRate} position="bottom-start" tag="span">
                  <InfoIcon size="small" label="pending-invoice-info" />
                </Tooltip>
              </span>
            )}
          </td>
        );
      }
      default:
        return '';
    }
  };

  const getOverageLineItemRow = (overageItem: any) => {
    overageItem.discounts = flatten(overageItem.lineItems.map((lineItem: any) => lineItem.discounts) ?? []);
    overageItem.taxItems = flatten(overageItem.lineItems.map((lineItem: any) => lineItem.taxItems) ?? []);

    if (isDesignIteration1Enabled && invoice.sourceSystem === SourceSystem.CCP) {
      return (
        <>
          <td>
            <div className="invoice-details-col">
              <div className="overage-description">
                <span>Overages for last month</span>
              </div>
            </div>
          </td>
          <td
            className={classNames({
              'currency-code-header': isDesignIteration1Enabled,
            })}
            data-testid="item-amount-cell"
          >
            <FormatCurrency
              value={overageItem.totalAccruedSubtotal ? overageItem.totalAccruedSubtotal : 0}
              currency={currency}
              showCurrencyCode={!isDesignIteration1Enabled}
              showCommas
            />
          </td>
          {shouldShowPartnerMarginColumn && getColumnData(overageItem, InvoiceLineItemsTable.PARTNER_MARGIN)}
          {canShowPromotionOnInvoice && (
            <>
              {getColumnData(overageItem, InvoiceLineItemsTable.DISCOUNT_APPLIED)}
              {getColumnData(overageItem, InvoiceLineItemsTable.AMOUNT_EXCL_TAX)}
              {getColumnData(overageItem, InvoiceLineItemsTable.AMOUNT_INCL_TAX)}
            </>
          )}
        </>
      );
    }
    return (
      <>
        <td>
          <div className="invoice-details-col">
            <div className="overage-description">
              <span>Overages for last month</span>
            </div>
          </div>
        </td>
        <td>
          <NoDataContentInTable />
        </td>
        <td>
          <NoDataContentInTable />
        </td>
        {isCustomerContactEnabled && <NoDataContentInTable />}
        <td>
          <NoDataContentInTable />
        </td>
        <td data-testid="quantity-cell">
          <span>
            {overageItem.totalUsersAdded ?? 0}{' '}
            {getChargeElement({
              productId: overageItem?.productKey,
              ...(Number(overageItem?.totalUsersAdded) > 1 && { labelPlural: true }),
              edition: overageItem?.edition,
            })}
          </span>
        </td>
        <td
          className={classNames({
            'currency-code-header': isDesignIteration1Enabled,
          })}
          data-testid="item-amount-cell"
        >
          <FormatCurrency
            value={overageItem.totalAccruedSubtotal ? overageItem.totalAccruedSubtotal : 0}
            currency={currency}
            showCurrencyCode={!isDesignIteration1Enabled}
            showCommas
          />
        </td>
        {shouldShowPartnerMarginColumn && getColumnData(overageItem, InvoiceLineItemsTable.PARTNER_MARGIN)}
        {canShowPromotionOnInvoice && invoice.sourceSystem === SourceSystem.CCP && (
          <>
            {getColumnData(overageItem, InvoiceLineItemsTable.DISCOUNT_APPLIED)}
            {getColumnData(overageItem, InvoiceLineItemsTable.AMOUNT_EXCL_TAX)}
            {getColumnData(overageItem, InvoiceLineItemsTable.AMOUNT_INCL_TAX)}
          </>
        )}
      </>
    );
  };

  const getLineItemRow = (item: any, shouldHideSite = false) => {
    if (isDesignIteration1Enabled && invoice.sourceSystem === SourceSystem.CCP) {
      return (
        <>
          {getColumnData(item, InvoiceLineItemsTable.PRODUCT_DETAILS, shouldHideSite)}
          {isRedesignFastFollowEnabled && getColumnData(item, InvoiceLineItemsTable.SITE)}
          {getColumnData(item, InvoiceLineItemsTable.LIST_PRICE)}
          {shouldShowPartnerMarginColumn && getColumnData(item, InvoiceLineItemsTable.PARTNER_MARGIN)}
          {canShowPromotionOnInvoice && (
            <>
              {getColumnData(item, InvoiceLineItemsTable.DISCOUNT_APPLIED)}
              {getColumnData(item, InvoiceLineItemsTable.AMOUNT_EXCL_TAX)}
              {getColumnData(item, InvoiceLineItemsTable.AMOUNT_INCL_TAX)}
            </>
          )}
        </>
      );
    }

    return (
      <>
        {getColumnData(item, InvoiceLineItemsTable.PRODUCT_DETAILS)}
        {getColumnData(item, InvoiceLineItemsTable.ORGANIZATION)}
        {!shouldHideSite && getColumnData(item, InvoiceLineItemsTable.SITE)}
        {isCustomerContactEnabled && getColumnData(item, InvoiceLineItemsTable.CUSTOMER_CONTACTS)}
        {getColumnData(item, InvoiceLineItemsTable.BILLING_PERIOD)}
        {isCCPInvoice && getColumnData(item, InvoiceLineItemsTable.USAGE)}
        {getColumnData(item, InvoiceLineItemsTable.LIST_PRICE)}
        {shouldShowPartnerMarginColumn && getColumnData(item, InvoiceLineItemsTable.PARTNER_MARGIN)}
        {canShowPromotionOnInvoice && invoice.sourceSystem === SourceSystem.CCP && (
          <>
            {getColumnData(item, InvoiceLineItemsTable.DISCOUNT_APPLIED)}
            {getColumnData(item, InvoiceLineItemsTable.AMOUNT_EXCL_TAX)}
            {getColumnData(item, InvoiceLineItemsTable.AMOUNT_INCL_TAX)}
          </>
        )}
      </>
    );
  };

  const renderProductRow = ({
    items,
    coupledlineItemApps,
    creditLineItems,
    coupledlineItemAddOns,
    shouldGroupAddOns,
    excludeBorderBottom,
  }: {
    items: InvoiceLineItem[];
    coupledlineItemApps?: AppsGroupData;
    creditLineItems?: CreditItemsLinked;
    coupledlineItemAddOns?: AddOnsGroupData;
    shouldGroupAddOns?: boolean;
    excludeBorderBottom?: boolean;
  }) => {
    return items.map((lineItem: InvoiceLineItem) => {
      // @ts-ignore
      const hasOverageItem = Boolean(lineItem?.overage);
      const coupledAppsForProduct = lineItem?.entitlementId ? coupledlineItemApps?.[lineItem?.entitlementId] : null;
      const coupledAddOnsForProduct = lineItem?.entitlementId ? coupledlineItemAddOns?.[lineItem?.entitlementId] : null;
      // @ts-ignore
      const linkedCreditLineItems =
        lineItem?.entitlementNumber &&
        lineItem?.invoiceLineItemId &&
        (creditLineItems?.[lineItem?.entitlementNumber]?.[lineItem?.invoiceLineItemId] ?? []);
      return (
        <>
          {(linkedCreditLineItems || [])?.map((creditLineItem: InvoiceLineItem) => {
            return (
              <tr
                key={getLineItemKey(creditLineItem)}
                className={classNames('coupling-line-item', { 'with-overage': hasOverageItem || excludeBorderBottom })}
              >
                {getLineItemRow(creditLineItem, false)}
              </tr>
            );
          })}
          <tr
            key={getLineItemKey(lineItem)}
            className={classNames('coupling-line-item', { 'with-overage': hasOverageItem || excludeBorderBottom })}
          >
            {getLineItemRow(lineItem, false)}
          </tr>
          {/*@ts-ignore*/}
          {isSupportForOverageEnabled && lineItem.overage && (
            <tr className="coupling-line-item">
              {getOverageLineItemRow({
                // @ts-ignore
                ...lineItem.overage,
                productKey: lineItem.productKey,
                edition: lineItem.edition,
                isOverageItem: true,
              })}
            </tr>
          )}
          {shouldGroupAddOns && coupledAddOnsForProduct?.addOns?.length ? (
            <>
              {<div className="apps-heading">{`Add-ons`}</div>}
              {coupledAddOnsForProduct?.addOns?.map((appLineItem: InvoiceLineItem) => {
                return (
                  <tr key={getLineItemKey(appLineItem)} className="coupling-line-item white-row">
                    {getLineItemRow(appLineItem)}
                  </tr>
                );
              })}
            </>
          ) : null}
          {coupledAppsForProduct?.apps?.length ? (
            <>
              {coupledAppsForProduct?.groupName && (
                <div className="apps-heading">{`Apps for ${capitalize(coupledAppsForProduct?.groupName)}`}</div>
              )}
              {coupledAppsForProduct?.apps?.map((appLineItem: InvoiceLineItem) => {
                return (
                  <tr key={getLineItemKey(appLineItem)} className="coupling-line-item white-row">
                    {getLineItemRow(appLineItem)}
                  </tr>
                );
              })}
            </>
          ) : null}
        </>
      );
    });
  };

  const renderAppsAndAddOnsRows = ({
    items,
    groupName,
    shouldRenderAddOnsGroupName,
    showAppsAddOnsWithoutGroupName,
    excludeBorderBottom,
  }: {
    items: InvoiceLineItem[];
    groupName?: string;
    shouldRenderAddOnsGroupName?: boolean;
    showAppsAddOnsWithoutGroupName?: boolean;
    excludeBorderBottom?: boolean;
  }) => {
    if (isDesignIteration1Enabled) {
      const appRows = items.map((app: InvoiceLineItem) => {
        return (
          <AppSubRow
            key={getLineItemKey(app)}
            className={classNames('coupling-line-item white-row sub-row', { 'with-overage': excludeBorderBottom })}
          >
            {getLineItemRow(app, true)}
          </AppSubRow>
        );
      });

      return <AppRows isDefaultExpanded appsCount={items.length} subRows={appRows} />;
    }

    return (
      <>
        {groupName && (
          <div className="apps-heading">
            {shouldRenderAddOnsGroupName ? `Add-ons` : `Apps for ${capitalize(groupName)}`}
          </div>
        )}
        {showAppsAddOnsWithoutGroupName && (
          <div className="apps-heading">{shouldRenderAddOnsGroupName ? `Add-ons` : `Apps`}</div>
        )}
        {items.map((lineItem: InvoiceLineItem) => {
          return (
            <tr
              key={getLineItemKey(lineItem)}
              className={classNames('coupling-line-item white-row', { 'with-overage': excludeBorderBottom })}
            >
              {getLineItemRow(lineItem)}
            </tr>
          );
        })}
      </>
    );
  };

  const renderProcessedLineItems = (
    processedLineItems?: {
      [key: string]: {
        products: generatedTypesNew.InvoiceLineItems[];
        instances: generatedTypesNew.InvoiceLineItems[];
        apps: generatedTypesNew.InvoiceLineItems[];
        addOns: generatedTypesNew.InvoiceLineItems[];
      };
    } | null
  ) => {
    if (!processedLineItems) return null;
    return Object.keys(processedLineItems).map((key: string) => {
      const { products, instances, apps, addOns } = processedLineItems[key];

      return (
        <>
          {products?.length > 0 &&
            renderProductRow({
              items: products as InvoiceLineItem[],
              shouldGroupAddOns: shouldGroupAddOns,
              excludeBorderBottom: true,
            })}
          {instances?.length > 0 &&
            renderProductRow({
              items: instances as InvoiceLineItem[],
              shouldGroupAddOns: shouldGroupAddOns,
              excludeBorderBottom: true,
            })}
          {apps?.length > 0 &&
            renderAppsAndAddOnsRows({
              items: apps as InvoiceLineItem[],
              groupName: '',
              shouldRenderAddOnsGroupName: false,
              showAppsAddOnsWithoutGroupName: true,
              excludeBorderBottom: true,
            })}
          {addOns?.length > 0 &&
            renderAppsAndAddOnsRows({
              items: addOns as InvoiceLineItem[],
              groupName: '',
              shouldRenderAddOnsGroupName: true,
              showAppsAddOnsWithoutGroupName: true,
              excludeBorderBottom: true,
            })}
          <tr className="processed-line-item-separator" />
        </>
      );
    });
  };

  const renderSortedLineItemRows = (
    sortedCoupledlineItems: ProductsGroupContainer[] | null,
    shouldGroupAddOns?: boolean
  ) => {
    if (!sortedCoupledlineItems) return null;
    return sortedCoupledlineItems.map((coupledlineItem: ProductsGroupContainer) => {
      const coupledlineItemApps = coupledlineItem?.appsGroup ?? {};
      const coupledLineItemAddOns = coupledlineItem?.addOnsGroup ?? {};
      const creditLineItems = coupledlineItem?.creditItems ?? {};
      const linkedParentEntitlementId = defaultTo(coupledlineItem?.familyContainerId, '');
      return (
        <>
          {coupledlineItem?.products?.length > 0 && (
            <>
              {
                //@ts-ignore
                renderProductRow({
                  items: coupledlineItem?.products,
                  coupledlineItemApps: coupledlineItemApps,
                  creditLineItems: creditLineItems,
                  coupledlineItemAddOns: coupledLineItemAddOns,
                  shouldGroupAddOns: shouldGroupAddOns,
                })
              }
            </>
          )}
          {shouldGroupAddOns && coupledLineItemAddOns?.[linkedParentEntitlementId]?.addOns
            ? renderAppsAndAddOnsRows({
                items: coupledLineItemAddOns?.[linkedParentEntitlementId]?.addOns,
                groupName: coupledLineItemAddOns?.[linkedParentEntitlementId]?.groupName,
                shouldRenderAddOnsGroupName: true,
              })
            : null}
          {coupledlineItemApps?.[linkedParentEntitlementId]?.apps
            ? renderAppsAndAddOnsRows({
                items: coupledlineItemApps?.[linkedParentEntitlementId]?.apps,
                groupName: coupledlineItemApps?.[linkedParentEntitlementId]?.groupName,
              })
            : null}
        </>
      );
    });
  };

  const renderCoupledLineItems = (
    coupledlineItemsData: generatedTypesNew.ProductAppLineItemData,
    shouldGroupAddOns?: boolean,
    processedLineItems?: {
      [key: string]: {
        products: generatedTypesNew.InvoiceLineItems[];
        instances: generatedTypesNew.InvoiceLineItems[];
        apps: generatedTypesNew.InvoiceLineItems[];
        addOns: generatedTypesNew.InvoiceLineItems[];
      };
    } | null
  ) => {
    return isTWCPhase1Enabled
      ? renderProcessedLineItems(processedLineItems)
      : isSortLineItemsEnabled
      ? renderSortedLineItemRows(sortedCoupledlineItems, shouldGroupAddOns)
      : Object.keys(coupledlineItemsData).map((key: string) => {
          return (
            <>
              {
                //@ts-ignore
                coupledlineItemsData[key]?.products?.length > 0 && (
                  <>
                    {
                      //@ts-ignore
                      renderProductRow({ items: coupledlineItemsData[key].products })
                    }
                  </>
                )
              }
              {
                //@ts-ignore
                coupledlineItemsData[key]?.apps?.length > 0 &&
                  renderAppsAndAddOnsRows({
                    //@ts-ignore
                    items: coupledlineItemsData[key].apps,
                    groupName: key,
                  })
              }
            </>
          );
        });
  };

  const tableHeaders = getColumns(
    invoice.sourceSystem,
    canShowPromotionOnInvoice,
    isCustomerContactEnabled,
    shouldShowPartnerMarginColumn,
    currency,
    isDesignIteration1Enabled,
    isRedesignFastFollowEnabled
  );

  const getRefundLineItems = () => (
    <RefundResults
      refunds={invoice.refunds}
      source={invoice.sourceSystem}
      refundsBaseRoute={refundsBaseRoute}
      shouldDisableSidebar={shouldDisableRefundsSidebar}
      isDesignIteration1Enabled={isDesignIteration1Enabled}
    />
  );

  return (
    <div className="invoice-line-items-container">
      {isDesignIteration1Enabled && !isEmpty(invoice.refunds) && getRefundLineItems()}
      <div
        className={classNames('line-items-payment-details-container', {
          'line-items-payment-details-container-v2': isDesignIteration1Enabled,
        })}
      >
        {isDesignIteration1Enabled && <h4 className="invoice-breakdown">Invoice breakdown</h4>}
        <CustomDynamicResultTable
          emptyStateProps={emptyStateProps}
          noResults={lineItems.length <= 0}
          isLoading={false}
          error={''}
          testId={'invoice-line-items-table'}
          boxShadow={false}
        >
          <ColGroup columns={tableHeaders} />
          <TableHead columns={tableHeaders} />
          <TableBody>
            {!coupledLineItems
              ? lineItems.map((item: InvoiceLineItem) => {
                  // @ts-ignore
                  return (
                    <React.Fragment key={getLineItemKey(item)}>
                      <tr className="line-item">{getLineItemRow(item)}</tr>
                      {(item.products ?? []).map((product) => (
                        <tr key={getLineItemKey(product)} className="line-item child-item">
                          {isCCPInvoice && <td />}
                          <td className="pad-left">
                            {invoice.sourceSystem === SourceSystem.CCP && (
                              <div>{getIdentifier(product, invoice.sourceSystem)}</div>
                            )}
                            {product.description}
                          </td>
                          <td>
                            <formattedV2.FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={product.startDate} />-{' '}
                            <formattedV2.FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={product.endDate} />
                          </td>
                          <td data-testid="product-amount-cell">
                            <FormatCurrency value={product.subtotal ? product.subtotal : 0} currency={currency} />
                          </td>
                        </tr>
                      ))}
                      {/*@ts-ignore*/}
                      {isSupportForOverageEnabled && item.overage && (
                        <tr className="line-item">
                          {getOverageLineItemRow({
                            // @ts-ignore
                            ...item.overage,
                            productKey: item.productKey,
                            edition: item.edition,
                            isOverageItem: true,
                          })}
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              : renderCoupledLineItems(coupledLineItems, shouldGroupAddOns, processedLineItems)}
            {getDstLineItem(invoice, tableHeaders, currency)}
          </TableBody>
        </CustomDynamicResultTable>
        <div className="invoice-amount-details">
          <div className="floated-right">
            <InvoiceAmountDetails
              invoice={invoice}
              canShowPromotionOnInvoice={canShowPromotionOnInvoice}
              isCCPInvoice={isCCPInvoice}
              {...(isDesignIteration1Enabled && { version: 2 })}
              isSupportForOverageEnabled={isSupportForOverageEnabled}
            />
          </div>
        </div>
        {!isPaymentMethodDetailsHidden && (
          <div className="invoice-payment-details" data-testid="payment-details">
            <div className="floated-right">
              <InvoicePaymentDetails {...invoice} />
            </div>
          </div>
        )}
      </div>
      {!isDesignIteration1Enabled && !isNil(invoice?.memo) && (
        <div className="refund-creation-notes">
          <h4>Memo</h4>
          <div className="line-items-payment-details-container margin-top">
            <div className="custom-dynamic-result-table margin-0">
              <p>{invoice?.memo}</p>
            </div>
          </div>
        </div>
      )}
      {!isDesignIteration1Enabled && !isEmpty(invoice.refunds) && getRefundLineItems()}
    </div>
  );
};

export default InvoiceLineItems;
